/**
 * PhotostudioProjectPage Component
 *
 * Displays images within a single project.
 * Features:
 * - Image grid with selection
 * - Download selected images
 * - Delete selected images
 * - Back navigation
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Download,
  Trash2,
  CheckSquare,
  Square,
  Loader2,
  Image as ImageIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';

interface IPhotostudioProjectPageProps {
  projectId: Id<'photostudioProjects'>;
  onBack: () => void;
}

interface IProjectImage {
  _id: Id<'photostudioImages'>;
  storageId: Id<'_storage'>;
  imageUrl: string;
  prompt: string;
  createdAt: number;
}

export function PhotostudioProjectPage({ projectId, onBack }: IPhotostudioProjectPageProps) {
  // Queries
  const project = useQuery(api.photostudioProjects.getProjectById, { projectId });
  const imagesData = useQuery(api.photostudioImages.getImagesByProject, { projectId });
  const images: IProjectImage[] = (imagesData ?? []) as IProjectImage[];

  // Mutations
  const deleteImagesMutation = useMutation(api.photostudioImages.deleteImages);

  // State
  const [selectedIds, setSelectedIds] = useState<Set<Id<'photostudioImages'>>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isLoading = project === undefined || imagesData === undefined;
  const selectedCount = selectedIds.size;
  const allSelected = selectedCount === images.length && images.length > 0;
  const noneSelected = selectedCount === 0;

  // Toggle selection
  const toggleSelect = (imageId: Id<'photostudioImages'>) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  // Select all
  const selectAll = () => {
    setSelectedIds(new Set(images.map(img => img._id)));
  };

  // Deselect all
  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  // Download selected
  const handleDownloadSelected = async () => {
    const selectedImages = images.filter(img => selectedIds.has(img._id));

    for (let i = 0; i < selectedImages.length; i++) {
      const image = selectedImages[i];
      try {
        const response = await fetch(image.imageUrl);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${project?.name || 'project'}-${i + 1}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
      }
    }
  };

  // Delete selected
  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;

    setIsDeleting(true);
    const result = await deleteImagesMutation({
      imageIds: Array.from(selectedIds),
    });
    setIsDeleting(false);

    if (result.success) {
      setSelectedIds(new Set());
      setDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground">Project not found</p>
        <Button variant="link" onClick={onBack}>
          Go back to projects
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="size-5" />
          </Button>
          <div>
            <h2 className="text-xl font-semibold">{project.name}</h2>
            <p className="text-sm text-muted-foreground">
              {images.length} image{images.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {images.length > 0 && (
          <div className="flex items-center gap-2">
            {/* Select/Deselect All */}
            <Button
              variant="ghost"
              size="sm"
              onClick={allSelected ? deselectAll : selectAll}
              className="h-8 px-2 text-xs"
            >
              {allSelected ? (
                <>
                  <Square className="mr-1 size-3.5" />
                  Deselect All
                </>
              ) : (
                <>
                  <CheckSquare className="mr-1 size-3.5" />
                  Select All
                </>
              )}
            </Button>

            {/* Download */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadSelected}
              disabled={noneSelected}
              className="h-8 text-xs"
            >
              <Download className="mr-1 size-3.5" />
              Download ({selectedCount})
            </Button>

            {/* Delete */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={noneSelected}
              className="h-8 text-xs text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-1 size-3.5" />
              Delete ({selectedCount})
            </Button>
          </div>
        )}
      </div>

      {/* Images Grid */}
      {images.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <ImageIcon className="mb-4 size-16 text-muted-foreground/50" />
          <p className="text-lg font-medium">No images yet</p>
          <p className="text-sm text-muted-foreground">
            Generate some images and save them to this project
          </p>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {images.map(image => (
              <Card
                key={image._id}
                className={cn(
                  'group relative overflow-hidden transition-all',
                  selectedIds.has(image._id) && 'ring-2 ring-primary'
                )}
              >
                {/* Image */}
                <div
                  role="button"
                  tabIndex={0}
                  className="aspect-square cursor-pointer bg-muted"
                  onClick={() => toggleSelect(image._id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleSelect(image._id);
                    }
                  }}
                >
                  <img src={image.imageUrl} alt="Generated" className="size-full object-cover" />
                </div>

                {/* Selection Checkbox */}
                <div className="absolute left-2 top-2">
                  <Checkbox
                    checked={selectedIds.has(image._id)}
                    onCheckedChange={() => toggleSelect(image._id)}
                    className="size-5 border-2 bg-background/80 backdrop-blur-sm"
                  />
                </div>

                {/* Selection Overlay */}
                {selectedIds.has(image._id) && (
                  <div className="absolute inset-0 pointer-events-none bg-primary/10" />
                )}

                {/* Prompt Preview */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
                  <p className="line-clamp-2 text-xs text-white">{image.prompt}</p>
                </div>
              </Card>
            ))}
          </div>

          {/* Selected Count */}
          {selectedCount > 0 && (
            <p className="text-center text-xs text-muted-foreground">
              {selectedCount} of {images.length} images selected
            </p>
          )}
        </>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Images</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedCount} selected image
              {selectedCount !== 1 ? 's' : ''}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSelected}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="mr-2 size-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
